import { Elements } from '@tfo/common'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { CopyableInputField } from '../SensitiveInputField'
import { DestructiveOutlineButton, MappingRows, ScimToken, useScimStyles } from './scimShared'

const { Paragraph, Label } = Elements

type Props = {
  token: ScimToken | null
  revealedToken: string | null
  endpointUrl: string | null
  mappingExpanded: boolean
  onExpandMapping: () => void
  onRegenerateClick: () => void
  onDisableClick: () => void
  errorKey: string | null
}

export const ScimManageView: React.FC<Props> = ({
  token,
  revealedToken,
  endpointUrl,
  mappingExpanded,
  onExpandMapping,
  onRegenerateClick,
  onDisableClick,
  errorKey,
}) => {
  const intl = useIntl()
  const styles = useScimStyles()

  const formatDay = (date: string) =>
    intl.formatDate(date, { year: 'numeric', month: 'long', day: 'numeric' })

  return (
    <>
      {revealedToken && (
        <div className={styles.tokenBanner}>
          <div className={styles.tokenBannerTitle}>
            <ReportProblemOutlinedIcon fontSize='small' />
            {intl.formatMessage({ id: 'sso.scim.token.banner.title' })}
          </div>
          <Paragraph className={styles.tokenBannerSubtitle}>
            {intl.formatMessage({ id: 'sso.scim.token.banner.subtitle' })}
          </Paragraph>
          <CopyableInputField
            value={revealedToken}
            sensitive
            copyValueLabel={intl.formatMessage({ id: 'sso.scim.copy' })}
            copyValue={() => navigator.clipboard.writeText(revealedToken)}
          />
        </div>
      )}
      <div className={styles.section}>
        <Label>{intl.formatMessage({ id: 'sso.scim.endpoint.label' })}</Label>
        <CopyableInputField
          value={endpointUrl}
          copyValueLabel={intl.formatMessage({ id: 'sso.scim.copy' })}
          copyValue={() => navigator.clipboard.writeText(endpointUrl)}
        />
      </div>
      <div className={styles.section}>
        <Label>
          {intl.formatMessage({ id: 'sso.scim.token.label' })}
        </Label>
        <div className={styles.tokenGroup}>
          <div className={styles.tokenMeta}>
            {token && (
              <span>{intl.formatMessage({ id: 'sso.scim.token.created' })} {formatDay(token.createdAt)}</span>
            )}
            <span>
              {intl.formatMessage({ id: 'sso.scim.token.lastUsed' })}{' '}
              {token?.lastUsedAt
                ? formatDay(token.lastUsedAt)
                : '-'}
            </span>
          </div>
          <a className={styles.destructiveLink} onClick={onRegenerateClick}>
            {intl.formatMessage({ id: 'sso.scim.regenerate' })}
          </a>
        </div>
      </div>
      <div className={styles.section}>
        {!mappingExpanded && (
          <a className={styles.expanderLink} onClick={onExpandMapping}>
            + {intl.formatMessage({ id: 'sso.scim.mapping.show' })}
          </a>
        )}
        {mappingExpanded && (
          <table className={styles.mappingTable}>
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: 'sso.scim.mapping.column.scim' })}</th>
                <th>{intl.formatMessage({ id: 'sso.scim.mapping.column.tfo' })}</th>
              </tr>
            </thead>
            <tbody>
              {MappingRows.map((row) => (
                <tr key={row.scim}>
                  <td className={styles.mappingScimCell}>{row.scim}</td>
                  <td>{intl.formatMessage({ id: row.tfoKey })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <hr className={styles.divider} />
      <a className={styles.destructiveLink} onClick={onDisableClick}>
        {intl.formatMessage({ id: 'sso.scim.disable' })}
      </a>
      {errorKey && <Paragraph className={styles.errorText}>{intl.formatMessage({ id: errorKey })}</Paragraph>}
    </>
  )
}
